import React, { useState } from "react";
import {
  VideoActivityOption,
  IActivitySwitch,
  IVideoContentActivity,
} from "src/types";
import showToast from "@utils/toast";

import ActivityCore from "./activities.core";
import { useActivityCoreData } from "src/hooks/useActivityCoreData";
import { getAssetUrl, getNextSerialId } from "@utils/id.generator";
import { newActivity, updateActivity } from "src/service/activities.services";
import { Edit, Trash2, Save, X } from "lucide-react";
import { useNavigate } from "react-router-dom";

const VideoContentActivityForm = ({
  lessonMeta,
  initialActivity,
  activityId,
}: IActivitySwitch) => {
  const isEditing = !!initialActivity;

  const [isLoading, setLoading] = useState<boolean>(false);
  const [answer, setAnswer] = useState(initialActivity?.data.answer || "");
  const [newOption, setNewOption] = useState({ text: "", audioUrl: "" });
  const [editIndex, setEditIndex] = useState<number | null>(null);
  const [videoUrl, setVideoUrl] = useState(
    initialActivity?.data.question?.videoUrl || ""
  );
  const [options, setOptions] = useState<VideoActivityOption[]>(
    (initialActivity?.data.options as any as VideoActivityOption[]) || []
  );

  const navigate = useNavigate();

  const addOption = () => {
    if (!newOption.text.trim()) {
      return showToast("Text is required for each option", false);
    }
    const newId = getNextSerialId(options);
    const option: VideoActivityOption = {
      id: newId,
      text: newOption.text.trim(),
      audioUrl: newOption.audioUrl || undefined,
    };

    setOptions((prev) => [...prev, option]);
    setNewOption({ text: "", audioUrl: "" });
  };

  const QuestionOptionsState = options.map((opt) => {
    if (!opt.audioUrl) {
      return { id: opt.id, text: opt.text } as VideoActivityOption;
    }
    return {
      id: opt.id,
      text: opt.text,
      audioUrl: opt.audioUrl,
    } as VideoActivityOption;
  });

  // Core activity state
  const coreState = useActivityCoreData({
    instruction: initialActivity?.instruction,
    localizedInstruction: initialActivity?.localizedInstruction,
    difficulty: initialActivity?.difficulty,
    tags: initialActivity?.tags,
    isActive: initialActivity?.isActive || false,
    activityNumber: initialActivity?.activityNumber || 0,
  });

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    const isValidId = isEditing ? initialActivity?.id : activityId;
    if (!isValidId || isValidId === null || isValidId === undefined) {
      return showToast("Activity Id not provided", false);
    }

    const payload: IVideoContentActivity = {
      id: isValidId,
      activityKind: "VideoContentActivity",
      instruction: coreState.coreData.instruction,
      localizedInstruction: coreState.coreData.localizedInstruction,
      activityNumber: coreState.coreData?.activityNumber,
      lessonId: initialActivity?.lessonId ?? (lessonMeta?.lesson as string),
      difficulty: coreState.coreData.difficulty,
      tags: coreState.coreData.tags,
      isActive: coreState.coreData.isActive,
      data: {
        question: { videoUrl },
        options: QuestionOptionsState,
        answer: answer || "",
      },
    };

    const request = isEditing
      ? updateActivity(payload, isValidId)
      : newActivity(payload);
    const response = await request;
    if (response?.status !== "success") {
      showToast("There was error saving activity", false);
      setLoading(false);
    } else {
      setLoading(false);
      showToast(
        `${isEditing ? "Activity Updated" : "Activity Created"} successfully`,
        true
      );
      return navigate("/dashboard/levels/lessons/activities");
    }
    // console.log(payload);
  };

  return (
    <form
      onSubmit={handleSubmit}
      className="space-y-8 max-w-4xl mx-auto bg-white p-6 rounded-lg shadow-lg overflow-y-auto"
    >
      <h2 className="text-base font-bold text-gray-800">
        {isEditing ? "Edit Video Activity" : "Create Video Activity"}
      </h2>

      <ActivityCore data={coreState} />
      <div>
        <label className="text-sm font-medium text-gray-700">Video Code</label>
        <input
          value={videoUrl}
          onChange={(e) => setVideoUrl(e.target.value)}
          className="input-style mt-1"
          placeholder="Paste video Code"
          required
          maxLength={6}
          min={6}
        />
      </div>

      {videoUrl && (
        <div className="mt-3 rounded overflow-hidden border">
          <video
            src={getAssetUrl("video", videoUrl)}
            controls
            className="w-full rounded max-h-64 object-contain"
          />
        </div>
      )}

      <div className="space-y-4">
        <h3 className="text-sm font-bold text-gray-800">Add Option</h3>
        <div className="grid sm:grid-cols-2 gap-4">
          <input
            value={newOption.text}
            onChange={(e) =>
              setNewOption({ ...newOption, text: e.target.value })
            }
            className="input-style"
            placeholder="Option Text"
          />
          <input
            value={newOption.audioUrl}
            onChange={(e) =>
              setNewOption({ ...newOption, audioUrl: e.target.value })
            }
            maxLength={6}
            min={6}
            className="input-style"
            placeholder="Audio Code (optional)"
          />
        </div>
        <button
          type="button"
          onClick={addOption}
          className="text-sm text-brand font-medium hover:underline mt-1"
        >
          + Add Option
        </button>
      </div>

      {options.length > 0 && (
        <div className="space-y-4 pt-3">
          <h3 className="text-sm font-semibold text-gray-800">
            Option Preview
          </h3>
          <div className="flex overflow-x-auto gap-4">
            {options.map((opt, idx) => {
              const isEditing = editIndex === idx;
              return (
                <div
                  key={opt.id}
                  className="min-w-[220px] flex-shrink-0 bg-white border border-gray-200 rounded-lg shadow-md p-4 space-y-3"
                >
                  {isEditing ? (
                    <>
                      <input
                        type="text"
                        value={opt.text}
                        onChange={(e) =>
                          setOptions((prev) =>
                            prev.map((o, i) =>
                              i === idx ? { ...o, text: e.target.value } : o
                            )
                          )
                        }
                        className="w-full px-2 py-1 border rounded-md focus:ring focus:ring-brand"
                        placeholder="Option Text"
                      />
                      <input
                        type="text"
                        value={opt.audioUrl || ""}
                        onChange={(e) =>
                          setOptions((prev) =>
                            prev.map((o, i) =>
                              i === idx ? { ...o, audioUrl: e.target.value } : o
                            )
                          )
                        }
                        className="w-full px-2 py-1 border rounded-md focus:ring focus:ring-brand"
                        placeholder="Audio Code"
                      />
                      {/* {opt.audioUrl && (
                        <audio controls className="mt-2 w-full">
                          <source
                            src={getAssetUrl("audio", opt.audioUrl)}
                            type="audio/mpeg"
                          />
                        </audio>
                      )} */}
                      <div className="flex justify-end gap-2 pt-2">
                        <button
                          type="button"
                          onClick={() => setEditIndex(null)}
                          className="text-green-600 hover:text-green-800"
                          title="Save"
                        >
                          <Save size={18} />
                        </button>
                        <button
                          type="button"
                          onClick={() => setEditIndex(null)}
                          className="text-gray-500 hover:text-gray-700"
                          title="Cancel"
                        >
                          <X size={18} />
                        </button>
                      </div>
                    </>
                  ) : (
                    <>
                      <div className="flex justify-between">
                        <div>
                          <p className="text-xs text-gray-500">Text</p>
                          <p className="font-medium text-gray-800 truncate">
                            {opt.text}
                          </p>
                        </div>
                        <div>
                          <p className="text-xs text-gray-500">Code</p>
                          <p className="font-medium text-gray-800 truncate">
                            {opt.audioUrl || "—"}
                          </p>
                        </div>
                      </div>

                      {opt.audioUrl && (
                        <audio controls className="w-full rounded">
                          <source
                            src={getAssetUrl("audio", opt.audioUrl)}
                            type="audio/mpeg"
                          />
                        </audio>
                      )}

                      <div className="flex justify-between items-center border-t pt-2">
                        <label className="inline-flex items-center gap-1 text-xs text-gray-600">
                          <input
                            type="radio"
                            name="correctAnswer"
                            checked={answer === opt.id}
                            onChange={() => setAnswer(opt.id)}
                            className="scale-90 text-emerald-600"
                          />
                          Correct
                        </label>
                        <div className="flex gap-2">
                          <button
                            type="button"
                            onClick={() => setEditIndex(idx)}
                            className="text-blue-500 hover:text-blue-700"
                            title="Edit"
                          >
                            <Edit size={18} />
                          </button>
                          <button
                            type="button"
                            onClick={() =>
                              setOptions((prev) =>
                                prev.filter((_, i) => i !== idx)
                              )
                            }
                            className="text-red-500 hover:text-red-700"
                            title="Remove"
                          >
                            <Trash2 size={18} />
                          </button>
                        </div>
                      </div>
                    </>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}

      <button type="submit" className="button-primary" disabled={isLoading}>
        {isEditing ? "Update Activity" : "Create Activity"}
      </button>
    </form>
  );
};

export default VideoContentActivityForm;
